// theme/palette.ts
// Centralized color definitions based on Figma design system

import { PaletteOptions } from '@mui/material/styles';

// Extend the palette to include custom background properties
declare module '@mui/material/styles' {
  interface TypeBackground {
    card?: string;
    elevated?: string;
  }
}

export const palette: PaletteOptions = {
  mode: 'dark',

  // Primary - Cyan
  primary: {
    main: '#00FFD4',
    light: '#33FFE0',
    dark: '#00B89C',
    contrastText: '#0A0E14',
  },

  // Secondary - Purple
  secondary: {
    main: '#7B68EE',
    light: '#9D8FFF',
    dark: '#5A4AD1',
    contrastText: '#FFFFFF',
  },

  // Error - Magenta/Red
  error: {
    main: '#FF3366',
    light: '#FF6B8A',
    dark: '#CC294F',
    contrastText: '#FFFFFF',
  },

  // Warning - Yellow
  warning: {
    main: '#FFE600',
    light: '#FFF033',
    dark: '#CCB800',
    contrastText: '#0A0E14',
  },

  // Success - Green
  success: {
    main: '#00FF88',
    light: '#33FFA0',
    dark: '#00CC6D',
    contrastText: '#0A0E14',
  },

  // Info - Light Blue
  info: {
    main: '#00D4FF',
    light: '#33DFFF',
    dark: '#00A3CC',
    contrastText: '#0A0E14',
  },

  // Backgrounds
  background: {
    default: '#0A0E14',
    paper: '#1E2530',
    card: '#252D3A',
    elevated: '#2E3744',
  },

  // Text
  text: {
    primary: '#FFFFFF',
    secondary: '#8892A0',
    disabled: '#4A5568',
  },

  // Dividers
  divider: 'rgba(255, 255, 255, 0.1)',

  // Action states
  action: {
    active: '#00FFD4',
    hover: 'rgba(0, 255, 212, 0.08)',
    selected: 'rgba(0, 255, 212, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
};

// Table status color type
interface TableStatusColor {
  main: string;
  bg: string;
}

// Custom colors interface
export interface CustomColors {
  orange: string;
  comment: string;
  featured: string;
  tableStatus: {
    playing: TableStatusColor;
    finished: TableStatusColor;
    draw: TableStatusColor;
    oneNoShow: TableStatusColor;
    bothNoShow: TableStatusColor;
    appealOpen: TableStatusColor;
    appealProgress: TableStatusColor;
    dropOut: TableStatusColor;
    callInProgress: TableStatusColor;
    stillPlaying: TableStatusColor;
    noPlayers: TableStatusColor;
    missing: TableStatusColor;
    featured: TableStatusColor;
    hasComment: TableStatusColor;
  };
  roles: Record<string, string>;
  glows: {
    primary: string;
    danger: string;
    warning: string;
    success: string;
    info: string;
  };
}

// Additional custom colors for specific use cases
export const customColors: CustomColors = {
  // Accent colors
  orange: '#FF6B35',
  comment: '#FF8C42',
  featured: '#00D4FF',

  // Table status colors
  tableStatus: {
    playing: { main: '#00FF88', bg: 'rgba(0, 255, 136, 0.15)' },
    finished: { main: '#8892A0', bg: 'rgba(136, 146, 160, 0.15)' },
    draw: { main: '#00FFD4', bg: 'rgba(0, 255, 212, 0.15)' },
    oneNoShow: { main: '#FF6B35', bg: 'rgba(255, 107, 53, 0.15)' },
    bothNoShow: { main: '#FF3366', bg: 'rgba(255, 51, 102, 0.15)' },
    appealOpen: { main: '#FFE600', bg: 'rgba(255, 230, 0, 0.15)' },
    appealProgress: { main: '#7B68EE', bg: 'rgba(123, 104, 238, 0.15)' },
    dropOut: { main: '#FF3366', bg: 'rgba(255, 51, 102, 0.15)' },
    callInProgress: { main: '#FFE600', bg: 'rgba(255, 230, 0, 0.15)' },
    stillPlaying: { main: '#00FF88', bg: 'rgba(0, 255, 136, 0.15)' },
    noPlayers: { main: '#FF3366', bg: 'rgba(255, 51, 102, 0.15)' },
    missing: { main: '#7B68EE', bg: 'rgba(123, 104, 238, 0.15)' },
    featured: { main: '#00D4FF', bg: 'rgba(0, 212, 255, 0.15)' },
    hasComment: { main: '#FF8C42', bg: 'rgba(255, 140, 66, 0.15)' },
  },

  // Role colors
  // Canonical values mirror ROLE_COLORS in src/types/roles.types.ts
  roles: {
    admin: '#7B68EE',
    Store: '#00FFD4',
    'Head Judge': '#FFE600',
    'Floor Judge': '#00FF88',
    'Team Lead': '#FF6B35',
    Scorekeeper: '#00D4FF',
    'Deck Check': '#7B68EE',
    'Feature Match': '#FF00FF',
    Shield: '#FF3366',
    Manager: '#00FFD4',
    Stream: '#9C27B0',
  },

  // Glow effects
  glows: {
    primary: '0 0 20px rgba(0, 255, 212, 0.3)',
    danger: '0 0 20px rgba(255, 51, 102, 0.3)',
    warning: '0 0 20px rgba(255, 230, 0, 0.3)',
    success: '0 0 20px rgba(0, 255, 136, 0.3)',
    info: '0 0 20px rgba(123, 104, 238, 0.3)',
  },
};

// Gradients interface
export interface Gradients {
  primary: string;
  secondary: string;
  warning: string;
  danger: string;
  background: string;
}

// Gradients
export const gradients: Gradients = {
  primary: 'linear-gradient(90deg, #00FFD4, #00D4FF)',
  secondary: 'linear-gradient(90deg, #7B68EE, #00FFD4)',
  warning: 'linear-gradient(90deg, #FFE600, #FF6B35)',
  danger: 'linear-gradient(90deg, #FF3366, #FF6B35)',
  background: 'radial-gradient(ellipse at top, #151B23 0%, #0A0E14 70%)',
};
